import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Divider, Grid, IconButton, Stack, Tooltip, Typography, } from "@mui/material";
import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrievePlayerFeed } from "../api/FeedApiProvider";
import { useRetrieveLeaguesByPlayerId } from "../api/LeagueApiProvider";
import { useDeletePlayer, useRetrieveTeamsByPlayerId, useRetrieveUserPlayerPermissions, } from "../ApiProvider";
import { UserContext } from "../auth/UserProvider";
import { Feed } from "../feed/Feed";
import { LeaguesCard } from "../leagues/LeaguesCard";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { TeamsCard } from "../teams/TeamsCard";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { playerDisplayName } from "./PlayerDisplayNameLink";
import { PlayerStatsCard } from "./PlayerStatsCard";
import { PlayerView } from "./PlayerView";
export const PlayerPage = ({ player, canViewTeams, canModify, onGetStarted, }) => {
    const navigate = useNavigate();
    const [showDelete, setShowDelete] = React.useState(false);
    const { data: userPlayerPermissions, isLoading: userPlayerPermissionsIsLoading, error: userPlayerPermissionsError, } = useRetrieveUserPlayerPermissions({ playerId: player.id });
    const { data: teams = [], isLoading: teamsIsLoading, error: teamsError, } = useRetrieveTeamsByPlayerId({
        playerId: player.id,
        enabled: canViewTeams,
    });
    const teamIds = teams?.map((team) => team.id) || [];
    const mutation = useDeletePlayer({
        id: player.id,
        teamIds,
        onMutate: () => { },
        onSuccess: () => {
            return navigate(`/`);
        },
        onError: () => { },
    });
    const { user: contextUser, loading: isContextUserLoading } = useContext(UserContext);
    const isOwnProfile = contextUser?.pk === player.user?.pk;
    const { data: leagues = [], isLoading: leaguesIsLoading, error: leaguesError, } = useRetrieveLeaguesByPlayerId({ playerId: player.id });
    const { data: feed, isLoading: isFeedLoading, error: feedError, fetchNextPage, hasNextPage, isFetchingNextPage, } = useRetrievePlayerFeed({ player, limit: 10 });
    if (userPlayerPermissionsError) {
        return _jsx(ErrorView, { error: userPlayerPermissionsError });
    }
    if ((teamsIsLoading && canViewTeams) ||
        leaguesIsLoading ||
        mutation.isLoading ||
        userPlayerPermissionsIsLoading ||
        isFeedLoading ||
        isContextUserLoading) {
        return _jsx(Loading, {});
    }
    if (!player ||
        teamsError ||
        leaguesError ||
        feedError ||
        !userPlayerPermissions ||
        !feed) {
        return (_jsx(ErrorView, { error: teamsError || leaguesError || feedError || undefined }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: mutation.isError, message: "Failed to delete player. Please try again." }), _jsx(DeleteDialog, { open: showDelete, onCancel: () => setShowDelete(false), title: `Delete ${playerDisplayName(player)}?`, onConfirm: () => mutation.mutate(), "aria-labelledby": "alert-dialog-title", "aria-describedby": "alert-dialog-description" }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsxs(Grid, { item: true, xs: 10, children: [_jsx(PlayerView, { player: player }), _jsx(Typography, { variant: "body2", fontStyle: "italic", mt: 1, children: player.bio })] }), _jsx(Grid, { item: true, xs: 2, children: player.externalUser && canModify && (_jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(IconButton, { "aria-label": "edit", children: _jsx(Link, { to: `/external-users/${player.externalUser.id}/update/`, children: _jsx(EditIcon, {}) }) }), !player.user && (_jsx(Tooltip, { title: teams.length > 1
                                        ? "This player is on multiple teams. Remove them from each team first."
                                        : "", children: _jsx("span", { children: _jsx(IconButton, { "aria-label": "delete", disabled: mutation.isLoading || teams.length > 1, onClick: () => {
                                                setShowDelete(true);
                                            }, children: _jsx(DeleteIcon, {}) }) }) }))] })) })] }), _jsx(Divider, { sx: { margin: "20px" } }), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 4, children: _jsx(PlayerStatsCard, { player: player, canModify: canModify }) }), _jsxs(Grid, { item: true, xs: 12, md: 5, sx: { order: { xs: 3, md: 2 } }, children: [_jsx(Typography, { variant: "h6", children: "Latest Activity" }), _jsx(Feed, { feed: feed, fetchNextPage: fetchNextPage, hasNextPage: hasNextPage, isFetchingNextPage: isFetchingNextPage, onGetStarted: isOwnProfile ? onGetStarted : undefined })] }), _jsxs(Grid, { item: true, xs: 12, md: 3, sx: { order: { xs: 2, md: 3 } }, children: [_jsx(TeamsCard, { teams: teams
                                    .filter((team) => !!team.manager || team.players?.includes(player.id))
                                    .sort((a, b) => a.name.localeCompare(b.name)), isOwnProfile: isOwnProfile }), _jsx(Box, { sx: { mt: 2 }, children: _jsx(LeaguesCard, { leagues: leagues, isOwnProfile: isOwnProfile }) })] })] })] }));
};
